import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AuthStore } from '../store/auth.store';
import { StorageService } from '../services/storage.service';
import { TokenRefreshService } from '../services/token-refresh.service';
import { PermissionService } from '../services/permission.service';
import { CompanyContextStore } from '../store/company-context.store';

/** Rutas de admin: el superadmin puede acceder y se limpia el contexto de empresa. */
const SUPERADMIN_ADMIN_PREFIXES = ['/app/admin/company'];

/** Rutas a las que el superadmin puede acceder sin contexto de empresa (sin limpiar contexto). */
const SUPERADMIN_ALLOWED_PREFIXES = ['/app/configuration'];

export const authGuard: CanActivateFn & CanActivateChildFn = (
  _route,
  state,
): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const authService    = inject(AuthService);
  const authStore      = inject(AuthStore);
  const storageService = inject(StorageService);
  const tokens         = inject(TokenRefreshService);
  const permService    = inject(PermissionService);
  const companyCtx     = inject(CompanyContextStore);
  const router         = inject(Router);

  const goToLogin = (reason?: string): UrlTree =>
    router.createUrlTree(['/auth/sing-in'], {
      queryParams: reason ? { reason, returnUrl: state.url } : undefined,
    });

  const resolveAccess = (): boolean | UrlTree => {
    if (authService.requiresPasswordChange()) {
      return router.createUrlTree(['/auth/change-password']);
    }

    const url = state.url;

    // Entrar al área de administración de empresas siempre limpia el contexto activo.
    if (SUPERADMIN_ADMIN_PREFIXES.some((prefix) => url.startsWith(prefix))) {
      companyCtx.exit();
      return true;
    }

    // Rutas permitidas al superadmin sin contexto (sin limpiar contexto).
    if (SUPERADMIN_ALLOWED_PREFIXES.some((prefix) => url.startsWith(prefix))) {
      return true;
    }

    // Superadmin sin empresa seleccionada no puede acceder a rutas workspace.
    if (permService.isSuperAdmin() && !companyCtx.isInContext()) {
      return router.createUrlTree(['/app/admin/company/list']);
    }

    return true;
  };

  if (!authService.isAuthenticated()) {
    return goToLogin();
  }

  // Un token vencido no cierra la sesión: primero se intenta renovarlo.
  if (authStore.isTokenExpired()) {
    return tokens.refresh().pipe(
      map(() => resolveAccess()),
      catchError(() => {
        authStore.clearSession();
        storageService.clearSession();
        return of(goToLogin('session_expired'));
      }),
    );
  }

  return resolveAccess();
};
